import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Shield, Cookie, BarChart3, Megaphone, Settings2, X } from "lucide-react";

const STORAGE_KEY = "flashweb_cookie_consent_v1";
const OPEN_EVENT = "flashweb:open-cookie-prefs";

export type CookieCategories = {
  necessary: true;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
};

type StoredConsent = {
  categories: CookieCategories;
  updatedAt: string;
  version: 1;
};

const DEFAULTS: CookieCategories = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
};

function readConsent(): StoredConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredConsent;
  } catch {
    return null;
  }
}

function saveConsent(categories: CookieCategories) {
  const payload: StoredConsent = {
    categories,
    updatedAt: new Date().toISOString(),
    version: 1,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

export function openCookiePreferences() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
}

export function CookieConsent() {
  const [mounted, setMounted] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState<CookieCategories>(DEFAULTS);

  useEffect(() => {
    setMounted(true);
    const existing = readConsent();
    if (existing) {
      setPrefs(existing.categories);
    } else {
      setShowBanner(true);
    }
    const onOpen = () => {
      const cur = readConsent();
      if (cur) setPrefs(cur.categories);
      setShowPrefs(true);
      setShowBanner(false);
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  if (!mounted) return null;

  const acceptAll = () => {
    const all: CookieCategories = { necessary: true, functional: true, analytics: true, marketing: true };
    setPrefs(all);
    saveConsent(all);
    setShowBanner(false);
    setShowPrefs(false);
  };

  const rejectNonEssential = () => {
    setPrefs(DEFAULTS);
    saveConsent(DEFAULTS);
    setShowBanner(false);
    setShowPrefs(false);
  };

  const savePrefs = () => {
    saveConsent({ ...prefs, necessary: true });
    setShowBanner(false);
    setShowPrefs(false);
  };

  return (
    <>
      {showBanner && !showPrefs && (
        <div
          role="dialog"
          aria-label="Preferenze cookie"
          className="fixed inset-x-0 bottom-0 z-[100] px-2 sm:px-6 sm:pb-6 animate-in fade-in slide-in-from-bottom-4 duration-300"
          style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}
        >
          <div className="mx-auto max-w-4xl overflow-hidden rounded-2xl border border-border/70 bg-background/95 shadow-2xl backdrop-blur-xl">
            <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:gap-5 sm:p-6">
              <div className="flex items-center gap-2 sm:block">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary sm:h-10 sm:w-10">
                  <Cookie className="h-4 w-4 sm:h-5 sm:w-5" />
                </div>
                <p className="font-semibold text-foreground text-sm sm:hidden">Rispettiamo la tua privacy</p>
              </div>
              <div className="min-w-0 flex-1 text-sm">
                <p className="hidden font-semibold text-foreground sm:block">Rispettiamo la tua privacy</p>
                <p className="text-[13px] leading-relaxed text-muted-foreground sm:mt-1 sm:text-sm">
                  Usiamo cookie tecnici e, con il tuo consenso, cookie di analisi e marketing.{" "}
                  <Link to="/cookie-policy" className="underline hover:text-foreground">
                    Cookie Policy
                  </Link>
                  {" · "}
                  <Link to="/privacy-policy" className="underline hover:text-foreground">
                    Privacy
                  </Link>
                  .
                </p>
                <div className="mt-3 grid grid-cols-2 gap-2 sm:mt-4 sm:flex sm:flex-wrap">
                  <button
                    type="button"
                    onClick={acceptAll}
                    className="col-span-2 inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition hover:opacity-90 sm:col-span-1"
                  >
                    Accetta tutti
                  </button>
                  <button
                    type="button"
                    onClick={rejectNonEssential}
                    className="inline-flex items-center justify-center rounded-full border border-border bg-background px-4 py-2.5 text-sm font-semibold text-foreground transition hover:bg-accent"
                  >
                    Rifiuta
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPrefs(true)}
                    className="inline-flex items-center justify-center gap-1.5 rounded-full border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent sm:border-0 sm:bg-transparent sm:text-muted-foreground sm:hover:text-foreground"
                  >
                    <Settings2 className="h-4 w-4" />
                    Personalizza
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPrefs && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Centro preferenze cookie"
          className="fixed inset-0 z-[110] flex items-end justify-center bg-black/60 p-0 backdrop-blur-sm animate-in fade-in duration-200 sm:items-center sm:p-6"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowPrefs(false);
          }}
        >
          <div className="w-full max-w-2xl overflow-hidden rounded-t-2xl border border-border bg-background shadow-2xl sm:rounded-2xl">
            <div className="flex items-center justify-between border-b border-border/60 px-6 py-4">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                <h2 className="font-display text-lg font-bold">Preferenze cookie</h2>
              </div>
              <button
                type="button"
                onClick={() => setShowPrefs(false)}
                className="rounded-full p-1.5 text-muted-foreground transition hover:bg-accent hover:text-foreground"
                aria-label="Chiudi"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto px-6 py-5">
              <p className="text-sm text-muted-foreground">
                Scegli quali categorie di cookie autorizzare. I cookie necessari non possono essere
                disattivati perché indispensabili al funzionamento del sito.
              </p>

              <div className="mt-5 space-y-3">
                <CategoryRow
                  icon={<Shield className="h-4 w-4" />}
                  title="Cookie necessari"
                  desc="Indispensabili per la sicurezza, la navigazione e il corretto funzionamento del sito. Non possono essere disattivati."
                  checked
                  disabled
                />
                <CategoryRow
                  icon={<Settings2 className="h-4 w-4" />}
                  title="Cookie funzionali"
                  desc="Consentono di ricordare le tue preferenze (lingua, valuta, layout) per un'esperienza personalizzata."
                  checked={prefs.functional}
                  onChange={(v) => setPrefs((p) => ({ ...p, functional: v }))}
                />
                <CategoryRow
                  icon={<BarChart3 className="h-4 w-4" />}
                  title="Cookie analytics"
                  desc="Raccolgono dati aggregati e anonimi su come utilizzi il sito, per aiutarci a migliorarlo."
                  checked={prefs.analytics}
                  onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                />
                <CategoryRow
                  icon={<Megaphone className="h-4 w-4" />}
                  title="Cookie marketing"
                  desc="Utilizzati per mostrarti contenuti e pubblicità pertinenti sui nostri canali e su siti terzi."
                  checked={prefs.marketing}
                  onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                />
              </div>
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-border/60 px-6 py-4 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={rejectNonEssential}
                className="inline-flex items-center justify-center rounded-full border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground transition hover:bg-accent"
              >
                Rifiuta non necessari
              </button>
              <button
                type="button"
                onClick={savePrefs}
                className="inline-flex items-center justify-center rounded-full border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground transition hover:bg-accent"
              >
                Salva preferenze
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition hover:opacity-90"
              >
                Accetta tutti
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function CategoryRow({
  icon,
  title,
  desc,
  checked,
  disabled,
  onChange,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-border/60 bg-card/40 p-4">
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        {icon}
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          <label className={`relative inline-flex h-6 w-11 shrink-0 ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}>
            <input
              type="checkbox"
              className="peer sr-only"
              checked={checked}
              disabled={disabled}
              onChange={(e) => onChange?.(e.target.checked)}
            />
            <span className="absolute inset-0 rounded-full bg-muted transition peer-checked:bg-primary" />
            <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-background shadow-sm transition peer-checked:translate-x-5" />
          </label>
        </div>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{desc}</p>
      </div>
    </div>
  );
}
